import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

// Estados do contexto
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Ações do reducer
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer de autenticação
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
};

// Context
const AuthContext = createContext();

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Função de login
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      const response = await api.post('/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        const { user, accessToken } = response.data.data;
        
        // Salvar token no localStorage
        localStorage.setItem('accessToken', accessToken);
        
        // Configurar token no axios
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token: accessToken }
        });

        toast.success('Login realizado com sucesso!');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao fazer login';
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: message
      });

      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Função de logout
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.log('Erro ao fazer logout no servidor:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('accessToken');
      delete api.defaults.headers.common['Authorization'];
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logout realizado com sucesso!');
    }
  };

  // Função para obter dados do usuário
  const getCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      
      if (response.data.success) {
        return response.data.data.user;
      }
    } catch (error) {
      console.log('Erro ao obter dados do usuário:', error);
      throw error;
    }
  };

  // Função para refresh do token
  const refreshToken = async () => {
    try {
      const response = await api.post('/auth/refresh-token');
      
      if (response.data.success) {
        const { user, accessToken } = response.data.data;
        
        // Atualizar token
        localStorage.setItem('accessToken', accessToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: { user, token: accessToken }
        });

        return true;
      }
    } catch (error) {
      console.log('Erro ao renovar token:', error);
      logout();
      return false;
    }
  };

  // Verificar autenticação ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        try {
          const user = await getCurrentUser();
          console.debug('[Auth] /auth/me OK', user);
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: { user, token }
          });
        } catch (error) {
          console.warn('[Auth] /auth/me falhou, tentando refresh', error?.response?.data || error?.message);
          // Token inválido, tentar refresh
          const refreshed = await refreshToken();
          console.debug('[Auth] refreshToken resultado', refreshed);
          if (!refreshed) {
            localStorage.removeItem('accessToken');
            delete api.defaults.headers.common['Authorization'];
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          }
        }
      } else {
        console.debug('[Auth] sem token local, setLoading=false');
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  // Interceptor para refresh automático
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && 
            error.response?.data?.code === 'TOKEN_EXPIRED' &&
            !originalRequest._retry) {
          console.warn('[Auth] 401 TOKEN_EXPIRED em', originalRequest.url);
          
          originalRequest._retry = true;
          
          const refreshed = await refreshToken();
          console.debug('[Auth] refresh após 401', refreshed);
          if (refreshed) {
            // Tentar novamente a requisição original
            return api(originalRequest);
          }
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          console.warn('[Auth] 401 genérico em', originalRequest.url, error.response?.data);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  // Limpar erro
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Verificar se usuário tem permissão por role (compatibilidade com sistema antigo)
  const hasRole = (allowedRoles) => {
    if (!state.user) return false;
    return allowedRoles.includes(state.user.role);
  };

  // Verificar se usuário tem permissão específica (usa permissions do banco)
  const hasPermission = (permission) => {
    if (!state.user?.permissions) return false;
    // all:full significa acesso a tudo
    if (state.user.permissions.includes('all:full')) return true;
    return state.user.permissions.includes(permission);
  };

  // Verificar se é admin (apenas GERENCIA)
  const isAdmin = () => {
    return hasRole(['GERENCIA']);
  };

  const value = {
    ...state,
    login,
    logout,
    getCurrentUser,
    refreshToken,
    clearError,
    hasRole,
    hasPermission,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export default AuthContext; 